import { useMemo, useRef, useCallback } from 'react';
import { Table, Tr, Th, Td, TableContainer, Box, Thead, Tbody, Flex } from '@chakra-ui/react';

import { TextBold } from 'UiKit/typography';
import { Condition, Element, Iteration } from 'Component/others';
import EmptyState from 'Component/empty-state';
import { AlertOutlineIcon, SortIcon } from 'Asset/icon';
import DataNotFound from 'Component/data-notfound';
import Tooltip from 'UiKit/tooltip';
import Pagination from 'UiKit/pagination';

import { generateStickyStyle, handleSortingData } from './helper';
import styles from './styes.module.css';

const TableComponent = ({
  columns,
  dataSource = [],
  onSort = () => {},
  sortingData,
  tableName = 'default',
  emptyActionText,
  emptyDescription,
  onEmptyStateAction,
  isDataExist,
  onRowClick,
  isCanCreate,
  headRowHeight = '64px',
  rowHeight = '64px',
  containerClassName = '',
  paddingBodyBox = '0 12px',
  verticalAlignBody = 'center',
  maxTableHeight,
  maxTableBodyHeight,
  pagination,
  onScrollReachBottom,
}) => {
  const theadRef = useRef(null);
  const tbodyRef = useRef(null);

  const onBodyScroll = useCallback(() => {
    if (theadRef.current) theadRef.current.scrollLeft = tbodyRef.current.scrollLeft;
    if (onScrollReachBottom && tbodyRef.current) {
      const el = tbodyRef.current;
      if (el.scrollHeight - el.scrollTop <= el.clientHeight + 10) {
        onScrollReachBottom();
      }
    }
  }, [onScrollReachBottom]);

  const onHeadScroll = useCallback(() => {
    if (tbodyRef.current) tbodyRef.current.scrollLeft = theadRef.current.scrollLeft;
  }, []);

  return (
    <TableContainer
      className={`${styles['table-component__container']} ${containerClassName}`}
      id="table-component__container"
      maxHeight={maxTableHeight || 'unset'}
      overflowY={maxTableHeight ? 'hidden' : 'unset'}
    >
      <Condition
        statement={isDataExist}
        fallback={
          <EmptyState
            background="var(--color-semantic-white)"
            isCanCreate={isCanCreate}
            actionText={emptyActionText}
            description={emptyDescription}
            onAction={onEmptyStateAction}
            height={maxTableBodyHeight ? maxTableBodyHeight : 'calc(100vh - 320px)'}
          />
        }
      >
        <Condition statement={dataSource?.length > 0} fallback={<DataNotFound background="var(--color-semantic-white)" height="calc(100vh - 320px)" />}>
          <Table className={styles['table__component']} id="table-component" tableLayout="fixed">
            <Thead
              ref={theadRef}
              display={maxTableBodyHeight ? 'block' : undefined}
              overflowY={maxTableBodyHeight ? 'scroll' : undefined}
              onScroll={maxTableBodyHeight ? onHeadScroll : undefined}
              sx={maxTableBodyHeight ? { '::-webkit-scrollbar': { width: 0, height: 0 }, scrollbarWidth: 'none' } : undefined}
            >
              {/* Table Head start  */}
              <Tr
                className={styles['table-head__row']}
                id={`table__head-${tableName}__row`}
                height={headRowHeight}
                display={maxTableBodyHeight ? 'table' : undefined}
                width={maxTableBodyHeight ? '100%' : undefined}
                tableLayout={maxTableBodyHeight ? 'fixed' : undefined}
              >
                <Iteration
                  items={columns}
                  render={({ key, name, width, position = 'space-between', sortKey, labelInfo, rightSticky, leftSticky, customizeTableHead }, colIndex) => {
                    const isSorted = sortingData !== null && sortingData?.field === sortKey;
                    const { tdStyle, boxStyle } = generateStickyStyle({ leftSticky, rightSticky });

                    return (
                      <Th {...tdStyle} key={`table__head-${key}-${colIndex}`} width={width} minWidth={width} maxWidth={width} className={styles['table__head']}>
                        <Box {...boxStyle} className={`${styles['table__head--box']}`} justifyContent={position} minHeight={headRowHeight} width="100%">
                          <Condition statement={!customizeTableHead} fallback={<Element value={customizeTableHead} />}>
                            <TextBold className={styles['table__head--text']}>{name}</TextBold>
                            <Flex justifyContent="space-between" alignItems="center" flex={labelInfo && sortKey ? 1 : ''}>
                              <Condition statement={labelInfo}>
                                <Tooltip label={labelInfo}>
                                  <AlertOutlineIcon width="16px" height="16px" color="var(--color-semantic-text)" />
                                </Tooltip>
                              </Condition>
                              <Condition statement={sortKey}>
                                <Tooltip label={!isSorted || sortingData?.order === 'desc' ? 'Click to sort ascending' : 'Click to sort descending'}>
                                  <SortIcon
                                    size="22px"
                                    color={isSorted ? 'var(--color-primary)' : 'var(--color-neutral40)'}
                                    onClick={() => handleSortingData({ sortingData, sortKey, onSort })}
                                    className={`${styles['table__head--sort']} ${isSorted && styles[`table__head--sort-${sortingData?.order}`]}`}
                                  />
                                </Tooltip>
                              </Condition>
                            </Flex>
                          </Condition>
                        </Box>
                      </Th>
                    );
                  }}
                />
              </Tr>
            </Thead>
            {/* Table Head End */}

            {/* Table Body Start  */}
            <Condition statement={dataSource?.length}>
              <Tbody
                ref={tbodyRef}
                maxHeight={maxTableBodyHeight || 'unset'}
                display={maxTableBodyHeight ? 'block' : undefined}
                overflowY={maxTableBodyHeight ? 'scroll' : 'unset'}
                onScroll={maxTableBodyHeight ? onBodyScroll : undefined}
                sx={maxTableBodyHeight ? { '::-webkit-scrollbar': { width: 0, height: 0 }, scrollbarWidth: 'none' } : undefined}
              >
                <Iteration
                  items={dataSource}
                  render={(data, index) => {
                    return (
                      <Tr
                        key={`table__row-${index}`}
                        minHeight={rowHeight}
                        className={styles['table__body--row']}
                        data-testid={`table__body-${tableName}__row`}
                        onClick={() => onRowClick?.(data, index)}
                        cursor={onRowClick ? 'pointer' : 'default'}
                        _hover={onRowClick ? { bg: 'var(--color-neutral10)' } : {}}
                        display={maxTableBodyHeight ? 'table' : undefined}
                        width={maxTableBodyHeight ? '100%' : undefined}
                        tableLayout={maxTableBodyHeight ? 'fixed' : undefined}
                      >
                        <Iteration
                          items={columns}
                          render={(item, colIndex) => {
                            const position = item?.position ?? 'flex-start';
                            const { tdStyle, boxStyle } = generateStickyStyle({ leftSticky: item?.leftSticky, rightSticky: item?.rightSticky });

                            return (
                              <Td
                                {...tdStyle}
                                key={`table__body-${item.key}-${index}-${colIndex}`}
                                width={item?.width}
                                minWidth={item?.width}
                                maxWidth={item?.width}
                                borderBottom={'none'}
                                className={`table__body--${colIndex} ${styles['table__body']}`}
                                verticalAlign={verticalAlignBody}
                                height={rowHeight === '100%' ? '1px' : undefined}
                              >
                                <Box
                                  {...boxStyle}
                                  className={`${styles['table__body--box']}`}
                                  justifyContent={position}
                                  onClick={(e) => {
                                    // Stop propagation if the click is on a button or its children
                                    if (e.target.closest('button') || e.target.tagName === 'BUTTON') {
                                      e.stopPropagation();
                                    }
                                  }}
                                  width="100%"
                                  minHeight={rowHeight}
                                  height={rowHeight || undefined}
                                  padding={paddingBodyBox}
                                >
                                  {item?.component({ value: data[item.key], values: data, index })}
                                </Box>
                              </Td>
                            );
                          }}
                        />
                      </Tr>
                    );
                  }}
                />
              </Tbody>
            </Condition>
            {/* Table Body End  */}
          </Table>
        </Condition>
      </Condition>
    </TableContainer>
  );
};

const MemoizedTableComponent = (props) => {
  const { pagination, ...tableProps } = props;

  const memoizedTable = useMemo(
    () => <TableComponent {...tableProps} />,
    /* eslint-disable */
    [tableProps?.dataSource, tableProps?.columns],
  );

  return (
    <Box className={styles['table-component__wrapper']} height={props?.tableComponentHeight || 'unset'}>
      {memoizedTable}
      <Condition statement={pagination?.totalData > 0 || pagination?.isLoading}>
        <Box className={styles['table-component__pagination']}>
          <Pagination
            currentPage={pagination?.currentPage}
            totalData={pagination?.totalData}
            onChange={pagination?.onChange}
            isLoading={pagination?.isLoading}
            pageSize={pagination?.pageSize}
            onSizeChange={pagination?.onSizeChange}
            isShowChangePageSize={pagination?.isShowChangePageSize}
          />
        </Box>
      </Condition>
    </Box>
  );
};

export default MemoizedTableComponent;
